import React, { useMemo } from 'react';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import classNames from 'classnames';
import ChevronDownIcon from 'icons/chevron-down-new.svg';
import ChevronUpIcon from 'icons/chevron-up-new.svg';
import FunnelIcon from 'icons/funnel.svg';
import NoResultsIcon from 'icons/noresults-xl.svg';
import PersonIcon from 'icons/person.svg';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import voca from 'voca';

import OutlinedButton from 'components/application/buttons/OutlinedButton';
import CircleQuestion from 'components/application/CircleQuestion';
import ConditionalWrapper from 'components/application/ConditionalWrapper';
import OptionChoice from 'components/application/OptionChoice';
import Options from 'components/application/Options';
import SmallStubPill from 'components/application/pills/SmallStubPill';
import TruncatedPill from 'components/application/pills/TruncatedPill';
import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import RowBar from 'components/application/RowBar';
import SearchField from 'components/application/SearchField';
import Table from 'components/application/Table';
import DotSkeleton from 'components/application/tableSkeletons/DotSkeleton';
import TextSkeleton from 'components/application/tableSkeletons/TextSkeleton';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import UserManagementTableRowActions from 'components/settings/users/UserManagementTableRowActions';

import { asFormattedDateTime } from 'components/helpers/dates';
import { personDisplayName } from 'components/helpers/users';

const divisionBreakpoint = 2;

const filterOptionsShape = PropTypes.shape({
  filterLabels: PropTypes.instanceOf(Map).isRequired,
  onAdd: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  selectedFilters: PropTypes.arrayOf(PropTypes.string).isRequired,
});

export default function UserManagementTable({
  areProjectsEnabled,
  domainUsers,
  filterOptions,
  isAtFullUserCapacity,
  isDataPending,
  isManagingMultiDivisions,
  isSamlSsoEnforced,
  isTrainingRegisterAllowed,
  onAddUserClick,
  onEditClick,
  searchOptions,
}) {
  const currentActor = useCurrentActor();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementRow',
  });

  const { t: i18nTTable } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementTable',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const { singularRAMSName } = currentActor.division.attributes;

  const buildColumnsArgs = {
    isManagingMultiDivisions,
    isSamlSsoEnforced,
    isTrainingRegisterAllowed,
    translations: {
      cells: {
        allDivisions: i18nTShared('allDivisions'),
        allowanceType: {
          fullUser: i18nTTable('allowanceType.types.fullUser'),
          noPermissions: i18nTTable('allowanceType.types.noPermissions'),
          personnelReporteeManager: i18nTTable(
            'allowanceType.types.personnelReporteeManager',
          ),
          ramsViewer: i18nTTable('allowanceType.types.ramsViewer', {
            interpolation: { escapeValue: false },
            singularRAMSName: voca.capitalize(singularRAMSName),
          }),
        },
        inviteExpired: i18nTShared('inviteExpired'),
        lastSeen: i18nT('status.accepted'),
        noDivisions: i18nT('divisions.none.pill'),
        pending: i18nTShared('pending'),
        pill: {
          administrator: i18nT('pill.administrator'),
          primary: i18nT('pill.primary'),
        },
      },
      headers: {
        allowanceType: i18nTTable('allowanceType.header'),
        allowanceTypeTooltip: i18nTTable('allowanceType.tooltip'),
        division: i18nTShared('division'),
        status: i18nTShared('status'),
        user: i18nTShared('user'),
      },
    },
  };

  const columns = useMemo(
    () => buildColumns(buildColumnsArgs),
    [JSON.stringify(buildColumnsArgs)],
  );

  const data = useMemo(() => {
    const users = domainUsers?.data || [];

    return isDataPending ? Array(users.length || 25).fill({}) : users;
  }, [domainUsers?.data, isDataPending]);

  const included = useMemo(
    () => domainUsers?.included || {},
    [domainUsers?.included],
  );

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    meta: {
      currentDivisionId: currentActor.division.id,
      included,
      isDataPending,
      onEditClick,
    },
  });

  return (
    <>
      <TableHeader
        {...{
          searchOptions,
          areProjectsEnabled,
          filterOptions,
          isAtFullUserCapacity,
          onAddUserClick,
        }}
      />
      <Table
        blankState={<NoUsersResult search={searchOptions.debouncedValue} />}
        headers={table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th
                className={classNames(
                  'tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900',
                  header.column.columnDef.meta?.classNames,
                )}
                key={header.id}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </th>
            ))}
          </tr>
        ))}
        rows={table.getRowModel().rows.map((row) => {
          const hasInviteExpired =
            row.original.attributes?.invitationStatus === 'expired';

          return (
            <tr
              className='tw-group/row tw-h-17.5 hover:tw-bg-grey-025'
              key={row.id}
            >
              {row.getVisibleCells().map((cell) => (
                <td
                  className={classNames(
                    'tw-border-grey-100',
                    hasInviteExpired && 'tw-bg-grey-025',
                    cell.column.columnDef.meta?.classNames,
                  )}
                  key={cell.id}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          );
        })}
      />
    </>
  );
}

UserManagementTable.propTypes = {
  areProjectsEnabled: PropTypes.bool.isRequired,
  domainUsers: PropTypes.object,
  filterOptions: filterOptionsShape.isRequired,
  isAtFullUserCapacity: PropTypes.bool.isRequired,
  isDataPending: PropTypes.bool.isRequired,
  isManagingMultiDivisions: PropTypes.bool.isRequired,
  isSamlSsoEnforced: PropTypes.bool.isRequired,
  isTrainingRegisterAllowed: PropTypes.bool.isRequired,
  onAddUserClick: PropTypes.func.isRequired,
  onEditClick: PropTypes.func.isRequired,
  searchOptions: PropTypes.object.isRequired,
};

function buildColumns({
  isManagingMultiDivisions,
  isSamlSsoEnforced,
  isTrainingRegisterAllowed,
  translations,
}) {
  return [
    {
      id: 'profilePhoto',
      header: '',
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <DotSkeleton />;

        const profilePhoto = userProfilePhoto({
          included: table.options.meta.included,
          user: row.original,
        });

        const { firstName, lastName } = row.original.attributes;

        return (
          <ProfilePhotoDisplay
            componentSize='sm'
            photoUrl={profilePhoto?.links?.croppedUrl}
            profileInitials={`${firstName[0]}${lastName[0]}`}
          />
        );
      },
      meta: { classNames: 'tw-w-13' },
    },
    {
      id: 'user',
      header: translations.headers.user,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        const { accessType, email, firstName, lastName } =
          row.original.attributes;

        return (
          <div className='tw-flex tw-flex-1 tw-flex-col'>
            <div className='truncated-text-container--outer-flex'>
              <span className='truncated-text-container--wrapped truncated-text-container--two-lines tw-mr-2.5 tw-text-m tw-font-medium tw-text-grey-900'>
                {personDisplayName({ attributes: { firstName, lastName } })}
              </span>
              {translations.cells.pill[accessType] && (
                <SmallStubPill className='tw-bg-cyan-025 tw-text-cyan-800'>
                  {translations.cells.pill[accessType]}
                </SmallStubPill>
              )}
            </div>
            <div className='truncated-text-container tw-text-s tw-font-normal tw-tracking-wide'>
              {email}
            </div>
          </div>
        );
      },
      meta: { classNames: 'tw-w-50' },
    },
    {
      id: 'status',
      header: translations.headers.status,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        const { currentSignInAt, invitationSentAt, invitationStatus } =
          row.original.attributes;

        const statusDate = {
          accepted: currentSignInAt,
          pending: invitationSentAt,
        }[invitationStatus];

        return (
          <div className='tw-flex tw-flex-1 tw-flex-col'>
            <div className='tw-text-m tw-font-medium'>
              {
                {
                  accepted: translations.cells.lastSeen,
                  expired: translations.cells.inviteExpired,
                  pending: translations.cells.pending,
                }[invitationStatus]
              }
            </div>
            <div className='tw-text-s tw-font-normal tw-tracking-wide'>
              {statusDate && asFormattedDateTime({ date: statusDate })}
            </div>
          </div>
        );
      },
      meta: { classNames: 'tw-w-35' },
    },
    isManagingMultiDivisions && {
      id: 'divisions',
      header: translations.headers.division,
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <UserDivisions
            divisions={userPermittedDivisions({
              included: table.options.meta.included,
              user: row.original,
            })}
            isAdminType={['administrator', 'primary'].includes(
              row.original.attributes.accessType,
            )}
            translations={translations}
            userId={row.original.id}
          />
        );
      },
      meta: { classNames: 'tw-w-45' },
    },
    {
      id: 'allowanceType',
      header: () => (
        <AllowanceTypeHeader
          label={translations.headers.allowanceType}
          tooltip={translations.headers.allowanceTypeTooltip}
        />
      ),
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return <TextSkeleton />;

        return (
          <AllowanceTypeCell
            allowanceType={row.original.attributes.allowanceType}
            translations={translations}
          />
        );
      },
      meta: { classNames: 'tw-w-45' },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row, table }) => {
        if (table.options.meta.isDataPending) return null;

        return (
          <UserManagementTableRowActions
            accessControl={userAccessControl({
              divisionId: table.options.meta.currentDivisionId,
              included: table.options.meta.included,
              user: row.original,
            })}
            domainUser={row.original}
            onEditClick={table.options.meta.onEditClick}
            permittedDivisionsCount={
              row.original.relationships.permittedDivisions.data.length
            }
            {...{
              isManagingMultiDivisions,
              isSamlSsoEnforced,
              isTrainingRegisterAllowed,
            }}
          />
        );
      },
      meta: { classNames: 'tw-w-12.5 tw-p-0' },
    },
  ].filter(Boolean);
}

function userProfilePhoto({ included, user }) {
  return Object.values(included.profilePhoto || {}).find(
    (profilePhoto) =>
      profilePhoto.relationships.personnel?.data?.id ===
      user.relationships.personnel?.data?.id,
  );
}

function userPermittedDivisions({ included, user }) {
  const divisions = user.relationships.permittedDivisions.data
    .map(({ id }) => included.company[id])
    .sort((divisionA, divisionB) =>
      divisionA.attributes.name.localeCompare(divisionB.attributes.name, 'en', {
        sensitivity: 'base',
      }),
    );

  const primaryDivision = divisions.find(
    (division) => division.attributes.primary,
  );

  if (primaryDivision) {
    return [
      primaryDivision,
      ...divisions.filter((division) => division !== primaryDivision),
    ];
  } else {
    return divisions;
  }
}

function UserDivisions({ divisions, isAdminType, translations, userId }) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementRow',
  });

  const shouldHideDivisions = divisions.length > divisionBreakpoint;

  const viewableDivisions =
    shouldHideDivisions ? divisions.slice(0, 1) : divisions;

  return (
    <span className='tw-flex tw-items-center'>
      {isAdminType ?
        <TruncatedPill className='tw-bg-grey-200 tw-text-grey-600'>
          {translations.cells.allDivisions}
        </TruncatedPill>
      : divisions.length === 0 ?
        <Tooltip
          placement='top'
          tooltip={
            <p className='tw-m-0 tw-p-0'>
              <Trans
                i18nKey='settings.users.UserManagementRow.divisions.none.tooltip'
                ns='components'
              />
            </p>
          }
          trigger='hover'
        >
          <TruncatedPill className='tw-bg-red-025 tw-text-red-800'>
            {translations.cells.noDivisions}
          </TruncatedPill>
        </Tooltip>
      : <>
          {viewableDivisions.map((division) => (
            <DivisionPill
              division={division}
              key={`user--${userId}_division--${division.id}`}
            />
          ))}
          {shouldHideDivisions && (
            <Tooltip
              className='tooltip-dark--text-left tooltip-dark--max-w-xxs'
              placement='left'
              tooltip={divisions
                .slice(divisionBreakpoint - 1)
                .map((division) => (
                  <>
                    {division.attributes.name}
                    <br />
                  </>
                ))}
              trigger='hover'
            >
              <span>
                {i18nT('divisions.more', { count: divisions.length - 1 })}
              </span>
            </Tooltip>
          )}
        </>
      }
    </span>
  );
}

function DivisionPill({ division }) {
  const { name } = division.attributes;

  const truncationLength = 10;

  return (
    <TruncatedPill className='tw-bg-grey-200 tw-text-grey-600'>
      <ConditionalWrapper
        condition={name.length > truncationLength}
        wrapper={(children) => (
          <Tooltip placement='left' tooltip={name} trigger='hover'>
            {children}
          </Tooltip>
        )}
      >
        {name}
      </ConditionalWrapper>
    </TruncatedPill>
  );
}

function AllowanceTypeHeader({ label, tooltip }) {
  return (
    <span className='tw-flex tw-items-center tw-gap-1'>
      {label}
      <Tooltip
        className='tooltip-dark--max-w-xxs'
        placement='top'
        tooltip={tooltip}
        trigger='hover'
      >
        <CircleQuestion size='sm' />
      </Tooltip>
    </span>
  );
}

AllowanceTypeHeader.propTypes = {
  label: PropTypes.string.isRequired,
  tooltip: PropTypes.string.isRequired,
};

function AllowanceTypeCell({ allowanceType, translations }) {
  const label = translations.cells.allowanceType[allowanceType];

  if (['noPermissions', 'ramsViewer'].includes(allowanceType))
    return <span className='tw-text-m tw-text-grey-900'>{label}</span>;

  return (
    <span
      className={classNames(
        'tw-inline-flex tw-items-center tw-rounded-md tw-px-2 tw-py-1 tw-text-s tw-font-medium tw-text-grey-900',
        allowanceType === 'fullUser' ? 'tw-bg-grey-100' : (
          'tw-border-1 tw-border-solid tw-border-grey-200 tw-bg-white'
        ),
      )}
    >
      {label}
    </span>
  );
}

AllowanceTypeCell.propTypes = {
  allowanceType: PropTypes.oneOf([
    'fullUser',
    'noPermissions',
    'personnelReporteeManager',
    'ramsViewer',
  ]).isRequired,
  translations: PropTypes.object.isRequired,
};

function userAccessControl({ divisionId, included, user }) {
  return Object.values(included.accessControl || {}).find(
    (accessControl) =>
      accessControl.relationships.user.data.id === user.id &&
      accessControl.relationships.company.data.id === divisionId,
  );
}

export function TableHeader({
  areProjectsEnabled,
  filterOptions,
  isAtFullUserCapacity,
  onAddUserClick,
  searchOptions,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.UserManagementTable',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <RowBar
      actions={
        <OutlinedButton
          color='grey'
          disabled={isAtFullUserCapacity && !areProjectsEnabled}
          onClick={onAddUserClick}
          size='sm'
        >
          <PersonIcon className='-tw-ml-2' height={24} width={24} />
          <span>{i18nTShared('addUser')}</span>
        </OutlinedButton>
      }
      content={
        <div className='tw-flex tw-items-center'>
          <SearchField
            name='user_search'
            onChange={searchOptions.onChange}
            onReset={searchOptions.onReset}
            placeholder={i18nT('search.placeholder')}
            value={searchOptions.value}
          />
          <div className='tw-ml-3'>
            {filterOptions.selectedFilters.length === 0 ?
              <Options
                btnToggle={(isOpen) => (
                  <button
                    className={classNames(
                      'tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-1.5 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100',
                      isOpen && 'tw-border-blue-100 tw-bg-blue-050',
                    )}
                    data-element-name='filter-options'
                    onClick={() => {}}
                    type='button'
                  >
                    <FunnelIcon height={12} width={12} />
                    <span>{i18nTShared('filter')}</span>
                    {isOpen ?
                      <ChevronUpIcon height={20} width={20} />
                    : <ChevronDownIcon height={20} width={20} />}
                  </button>
                )}
                listStyle='tw-w-52 [&_li]:tw-border-none tw-z-[1000]'
                toggleStyle='tw-inline-flex [&_ul]:tw-border-blue-100 [&_ul]:tw-rounded-lg'
              >
                {[...filterOptions.filterLabels].map(([filterKey, label]) => (
                  <OptionChoice
                    key={filterKey}
                    onClick={(setOptionsOpen) => {
                      setOptionsOpen(false);
                      filterOptions.onAdd(filterKey);
                    }}
                  >
                    <a className='tw-text-m tw-font-medium tw-tracking-auto tw-text-grey-700 hover:tw-text-grey-700'>
                      {label}
                    </a>
                  </OptionChoice>
                ))}
              </Options>
            : <button
                className='tw-group/filter tw-flex tw-h-8 tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border-1 tw-border-solid tw-border-blue-050 tw-bg-blue-025 tw-pl-2.5 tw-pr-2.5 tw-text-m tw-font-medium tw-text-grey-900 hover:tw-border-blue-100 hover:tw-bg-blue-050 active:tw-border-blue-200 active:tw-bg-blue-100'
                data-element-name='filter-options'
                onClick={filterOptions.onOpen}
                type='button'
              >
                <FunnelIcon height={12} width={12} />
                <span>
                  {filterOptions.selectedFilters.length > 1 ?
                    i18nTShared('filters')
                  : i18nTShared('filter')}
                </span>
                <span className='tw-flex tw-h-5 tw-w-5 tw-items-center tw-justify-center tw-rounded-full tw-bg-blue-050 tw-text-s tw-font-medium tw-tracking-wide tw-text-blue-900 group-hover/filter:tw-bg-blue-100 group-active/filter:tw-bg-blue-200'>
                  {filterOptions.selectedFilters.length}
                </span>
              </button>
            }
          </div>
        </div>
      }
      modifiers={['border-top-curved', 'border-bottom-none', 'large']}
    />
  );
}

TableHeader.propTypes = {
  areProjectsEnabled: PropTypes.bool.isRequired,
  filterOptions: filterOptionsShape.isRequired,
  isAtFullUserCapacity: PropTypes.bool.isRequired,
  onAddUserClick: PropTypes.func.isRequired,
  searchOptions: PropTypes.shape({
    onChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    value: PropTypes.string.isRequired,
  }).isRequired,
};

function NoUsersResult({ search }) {
  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <div
      className='section section--notice tw-rounded-t-none tw-border-grey-100 tw-bg-white'
      style={{ borderTop: 'none' }}
    >
      <NoResultsIcon
        className='[&_path]:tw-fill-grey-300'
        height={64}
        width={64}
      />
      <h2 className='section__header tw-text-l tw-font-semibold tw-text-grey-900'>
        {search ?
          i18nTShared('noResultsFoundForSearch', {
            interpolation: { escapeValue: false },
            search,
          })
        : i18nTShared('noResultsFound')}
      </h2>
    </div>
  );
}
